import type { Dataset, MeasurementPanel, PanelData } from "./api";
import type { DashboardResolution } from "./dashboardChartData";
import { formatISODate, structuralDateTimeParts } from "./localization";
import type { TrafficView } from "./trafficViews";

export type ReportGridSize = 1 | 2 | 4;

export type ReportElementRange = {
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  timezone: string;
};

export type ReportPanelDraft = {
  kind: "panel";
  draftID: string;
  measurementPointID: string;
  measurementPoint: string;
  panel: MeasurementPanel;
  dataset?: Dataset;
  data?: PanelData;
  trafficView?: TrafficView;
  timeProfileClassValues?: string[];
  title: string;
  comment: string;
  range: ReportElementRange;
  resolution: DashboardResolution;
  loadedQueryKey: string;
  status: "ready" | "loading" | "error";
  error?: string;
};

export type ReportImageDraft = {
  kind: "image";
  draftID: string;
  title: string;
  sourceName: string;
  dataURL: string;
  format: "PNG" | "JPEG";
  width: number;
  height: number;
  scaleMode: "contain" | "cover";
};

// Fest vorbelegtes Element eines Messstellenverbunds. Kennzahlen, Zeilen und
// Diagrammdaten werden im Verbundbereich mit denselben Funktionen wie im
// Dashboard vorbereitet, damit Dashboard und PDF dieselben Zahlen zeigen.
export type ReportGroupDraft = {
  kind: "group";
  draftID: string;
  title: string;
  meta: string[];
  lines?: string[];
  table?: { head: string[]; rows: string[][]; continued?: boolean };
  chart?: { panel: MeasurementPanel; data: PanelData };
};

export type ReportGroupContext = {
  name: string;
  campaign: string;
  range: string;
  resolution: string;
  notices: string[];
};

export type ReportElementDraft = ReportPanelDraft | ReportImageDraft | ReportGroupDraft;

export type ReportPageDraft = {
  draftID: string;
  panelsPerPage?: ReportGridSize;
  introduction?: boolean;
  conclusionSlot?: number;
  slots: Array<string | null>;
};

let fallbackID = 0;

export function createReportDraftID(prefix: "element" | "image" | "page" | "group") {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return `${prefix}-${crypto.randomUUID()}`;
  fallbackID += 1;
  return `${prefix}-${Date.now().toString(36)}-${fallbackID.toString(36)}`;
}

export function reportRangeQueryKey(range: ReportElementRange, resolution: DashboardResolution) {
  return `${range.fromDate}T${range.fromTime}|${range.toDate}T${range.toTime}|${range.timezone}|${resolution}`;
}

function zonedDateParts(date: Date, timeZone: string) {
  try {
    const parts = structuralDateTimeParts(date, timeZone);
    return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day), hour: Number(parts.hour), minute: Number(parts.minute), second: Number(parts.second) };
  } catch {
    return null;
  }
}

function timeSeconds(value: string) {
  if (value === "24:00") return 24 * 60 * 60;
  const match = /^(\d{2}):(\d{2})$/.exec(value);
  if (!match) return Number.NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60 ? hours * 3600 + minutes * 60 : Number.NaN;
}

function dateAtTime(dateValue: string, time: string, timeZone: string) {
  const seconds = timeSeconds(time);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateValue) || !Number.isFinite(seconds)) return null;
  const [year, month, day] = dateValue.split("-").map(Number);
  const target = new Date(Date.UTC(year, month - 1, day));
  target.setUTCSeconds(target.getUTCSeconds() + seconds);
  const targetTimestamp = target.getTime();
  let candidateTimestamp = targetTimestamp;
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = zonedDateParts(new Date(candidateTimestamp), timeZone);
    if (!parts) return null;
    const displayedTimestamp = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    if (displayedTimestamp === targetTimestamp) return new Date(candidateTimestamp);
    candidateTimestamp += targetTimestamp - displayedTimestamp;
  }
  return null;
}

export function resolveReportRange(range: ReportElementRange) {
  const from = dateAtTime(range.fromDate, range.fromTime, range.timezone);
  const to = dateAtTime(range.toDate, range.toTime, range.timezone);
  if (!from || !to || from >= to || to.getTime() - from.getTime() > 366 * 24 * 60 * 60 * 1000) return null;
  return { from, to };
}

export function reportRangeLabel(range: ReportElementRange) {
  return `${formatISODate(range.fromDate)} ${range.fromTime} – ${formatISODate(range.toDate)} ${range.toTime}`;
}

export function reportResolutionLabel(resolution: DashboardResolution) {
  if (resolution === 60) return "1 Minute";
  if (resolution === 900) return "15 Minuten";
  if (resolution === 3600) return "1 Stunde";
  if (resolution === 86400) return "1 Tag";
  return "Auto";
}

export function effectiveReportResolution(draft: Pick<ReportPanelDraft, "resolution" | "data">): DashboardResolution {
  if (draft.resolution !== "auto") return draft.resolution;
  const actualResolution = draft.data?.bucket_seconds;
  if (actualResolution === 60 || actualResolution === 900 || actualResolution === 3600 || actualResolution === 86400) return actualResolution;
  return "auto";
}

export function reportPanelResolutionLabel(draft: Pick<ReportPanelDraft, "resolution" | "data">) {
  return reportResolutionLabel(effectiveReportResolution(draft));
}

export function reportPanelContextKey(draft: Pick<ReportPanelDraft, "range" | "resolution" | "data">) {
  return reportRangeQueryKey(draft.range, effectiveReportResolution(draft));
}

export function effectivePageGrid(page: ReportPageDraft, globalGrid: ReportGridSize) {
  return page.panelsPerPage ?? globalGrid;
}

function reportPagePanelCapacity(page: ReportPageDraft, globalGrid: ReportGridSize) {
  return Math.max(0, effectivePageGrid(page, globalGrid) - (page.introduction ? 1 : 0));
}

export function setReportIntroduction(pages: ReportPageDraft[], inline: boolean, globalGrid: ReportGridSize) {
  if (!pages.length || Boolean(pages[0].introduction) === inline) return pages;
  const queue = pages.flatMap((page) => page.slots.filter((slot): slot is string => slot !== null));
  const next: ReportPageDraft[] = pages.map((page, index) => ({ ...page, introduction: index === 0 && inline ? true : undefined, slots: [] }));
  let pageIndex = 0;
  while (queue.length || pageIndex < next.length) {
    if (pageIndex >= next.length) next.push({ draftID: createReportDraftID("page"), slots: [] });
    const page = next[pageIndex];
    const capacity = reportPagePanelCapacity(page, globalGrid);
    page.slots = Array.from({ length: capacity }, () => queue.shift() ?? null);
    pageIndex += 1;
  }
  return withoutEmptyTail(next, globalGrid);
}

function reportGridColumns(gridSize: ReportGridSize, orientation: "portrait" | "landscape") {
  if (gridSize === 4) return 2;
  return gridSize === 2 && orientation === "landscape" ? 2 : 1;
}

export function setReportConclusion(pages: ReportPageDraft[], inline: boolean, globalGrid: ReportGridSize, orientation: "portrait" | "landscape") {
  const next: ReportPageDraft[] = pages.map((page) => ({ ...page, conclusionSlot: undefined, slots: [...page.slots] }));
  if (!inline) return next;
  let pageIndex = next.length - 1;
  while (pageIndex >= 0 && !next[pageIndex].slots.some((slot) => slot !== null)) pageIndex -= 1;
  if (pageIndex < 0) return next;
  const page = next[pageIndex];
  let lastPanelSlot = page.slots.length - 1;
  while (lastPanelSlot >= 0 && page.slots[lastPanelSlot] === null) lastPanelSlot -= 1;
  if (lastPanelSlot < 0) return next;
  const gridSize = effectivePageGrid(page, globalGrid);
  const introductionOffset = page.introduction ? 1 : 0;
  const conclusionGridIndex = introductionOffset + lastPanelSlot + reportGridColumns(gridSize, orientation);
  if (conclusionGridIndex >= gridSize) return next;
  const conclusionSlot = conclusionGridIndex - introductionOffset;
  if (conclusionSlot < 0 || conclusionSlot >= page.slots.length || page.slots[conclusionSlot] !== null) return next;
  page.conclusionSlot = conclusionSlot;
  return next;
}

export function createReportPages(elementIDs: string[], globalGrid: ReportGridSize): ReportPageDraft[] {
  if (!elementIDs.length) return [{ draftID: createReportDraftID("page"), slots: Array(globalGrid).fill(null) }];
  const pages: ReportPageDraft[] = [];
  for (let index = 0; index < elementIDs.length; index += globalGrid) {
    pages.push({
      draftID: createReportDraftID("page"),
      slots: [...elementIDs.slice(index, index + globalGrid), ...Array(Math.max(0, globalGrid - (elementIDs.length - index))).fill(null)].slice(0, globalGrid),
    });
  }
  return pages;
}

function withoutEmptyTail(pages: ReportPageDraft[], globalGrid: ReportGridSize) {
  const next = [...pages];
  while (next.length > 1 && next.at(-1)?.slots.every((slot) => slot === null)) next.pop();
  return next.length ? next : createReportPages([], globalGrid);
}

export function appendReportElement(pages: ReportPageDraft[], elementID: string, globalGrid: ReportGridSize) {
  const next = pages.map((page) => ({ ...page, slots: [...page.slots] }));
  for (const page of next) {
    const empty = page.slots.indexOf(null);
    if (empty >= 0) {
      page.slots[empty] = elementID;
      return next;
    }
  }
  return [...next, { draftID: createReportDraftID("page"), slots: [elementID, ...Array(globalGrid - 1).fill(null)] }];
}

export function removeReportElement(pages: ReportPageDraft[], elementID: string, globalGrid: ReportGridSize) {
  return withoutEmptyTail(pages.map((page) => ({ ...page, slots: page.slots.map((slot) => slot === elementID ? null : slot) })), globalGrid);
}

function cascadeIntoPages(pages: ReportPageDraft[], startPage: number, overflow: string[], globalGrid: ReportGridSize) {
  const followingIDs = pages.slice(startPage).flatMap((page) => page.slots.filter((slot): slot is string => slot !== null));
  const queue = [...overflow, ...followingIDs];
  let pageIndex = startPage;
  while (queue.length || pageIndex < pages.length) {
    if (pageIndex >= pages.length) pages.push({ draftID: createReportDraftID("page"), slots: [] });
    const page = pages[pageIndex];
    const capacity = reportPagePanelCapacity(page, globalGrid);
    page.slots = Array.from({ length: capacity }, () => queue.shift() ?? null);
    pageIndex += 1;
  }
}

export function setReportPageGrid(pages: ReportPageDraft[], pageID: string, override: ReportGridSize | undefined, globalGrid: ReportGridSize) {
  const next = pages.map((page) => ({ ...page, slots: [...page.slots] }));
  const pageIndex = next.findIndex((page) => page.draftID === pageID);
  if (pageIndex < 0) return next;
  const page = next[pageIndex];
  page.panelsPerPage = override;
  const capacity = reportPagePanelCapacity(page, globalGrid);
  const currentIDs = page.slots.filter((slot): slot is string => slot !== null);
  const overflow = currentIDs.slice(capacity);
  page.slots = [...currentIDs.slice(0, capacity), ...Array(capacity).fill(null)].slice(0, capacity);
  cascadeIntoPages(next, pageIndex + 1, overflow, globalGrid);
  return withoutEmptyTail(next, globalGrid);
}

export function setGlobalReportGrid(pages: ReportPageDraft[], globalGrid: ReportGridSize) {
  const next: ReportPageDraft[] = [];
  let overflow: string[] = [];
  const appendOverflowPages = () => {
    while (overflow.length) {
      next.push({ draftID: createReportDraftID("page"), slots: [...overflow.splice(0, globalGrid), ...Array(globalGrid).fill(null)].slice(0, globalGrid) });
    }
  };
  for (const sourcePage of pages) {
    const page = { ...sourcePage, slots: [...sourcePage.slots] };
    if (page.panelsPerPage !== undefined) {
      appendOverflowPages();
      next.push(page);
      continue;
    }
    const capacity = reportPagePanelCapacity(page, globalGrid);
    const ids = [...overflow, ...page.slots.filter((slot): slot is string => slot !== null)];
    page.slots = [...ids.slice(0, capacity), ...Array(capacity).fill(null)].slice(0, capacity);
    overflow = ids.slice(capacity);
    next.push(page);
  }
  appendOverflowPages();
  return withoutEmptyTail(next, globalGrid);
}

export function moveReportElement(pages: ReportPageDraft[], elementID: string, targetPageID: string, targetSlot: number, globalGrid: ReportGridSize) {
  const next = pages.map((page) => ({ ...page, slots: [...page.slots] }));
  const references = reportSlotReferences(next);
  const sourceIndex = references.findIndex((reference) => next.find((page) => page.draftID === reference.pageID)?.slots[reference.slot] === elementID);
  const targetIndex = references.findIndex((reference) => reference.pageID === targetPageID && reference.slot === targetSlot);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return pages;

  const valueAt = (index: number) => {
    const reference = references[index];
    return next.find((page) => page.draftID === reference.pageID)?.slots[reference.slot] ?? null;
  };
  const setValueAt = (index: number, value: string | null) => {
    const reference = references[index];
    const page = next.find((candidate) => candidate.draftID === reference.pageID);
    if (page) page.slots[reference.slot] = value;
  };

  if (valueAt(targetIndex) === null) {
    setValueAt(sourceIndex, null);
    setValueAt(targetIndex, elementID);
  } else if (sourceIndex < targetIndex) {
    for (let index = sourceIndex; index < targetIndex; index += 1) setValueAt(index, valueAt(index + 1));
    setValueAt(targetIndex, elementID);
  } else {
    for (let index = sourceIndex; index > targetIndex; index -= 1) setValueAt(index, valueAt(index - 1));
    setValueAt(targetIndex, elementID);
  }
  return withoutEmptyTail(next, globalGrid);
}

export function reportSlotReferences(pages: ReportPageDraft[]) {
  return pages.flatMap((page) => page.slots.flatMap((_, slot) => page.conclusionSlot === slot ? [] : [{ pageID: page.draftID, slot }]));
}
